import { useCallback, useEffect, useMemo } from "react";
import type { ResolvedLayer } from "@/domain/profile-resolver/index";
import type { Layers } from "@/domain/types";
import { openerFor, parentContext } from "@/games/sc2/sc2-registry";
import { isEditableTarget } from "@/platform/lib/editable-target";

/**
 * SC2 build-menu drill-down navigation: pressing or clicking an opener (Build Basic /
 * Build Advanced) descends into its submenu; Escape or the active Cancel hotkey backs a submenu out to
 * its worker card. Both directions live in the one drillNav(code), so a clicked cap navigates exactly
 * as the physical key does - the click path used to descend but never back out, stranding a player who
 * clicked Esc in a submenu. The physical-keyboard listener runs here, gated like the readout listener
 * (off while a modal owns the keyboard). A non-SC2 Game no-ops.
 */
export function useDrillNav({
  sc2,
  layers,
  activeModifierLayer,
  roster,
  context,
  modalOpen,
  setContextSel,
}: {
  sc2: boolean;
  layers: Layers;
  activeModifierLayer: ResolvedLayer;
  roster: string;
  context: string;
  modalOpen: boolean;
  setContextSel: (id: string) => void;
}) {
  // the codes the active Cancel command sits on, for backing out of a build submenu
  const cancelCodes = useMemo(
    () =>
      Object.entries(layers.base)
        .filter(([, b]) => b.cmd === "cancel")
        .map(([c]) => c),
    [layers],
  );

  // navigate the command card from one code, whether it arrived as a keypress or a cap click. Inside
  // a submenu, Escape and the active Cancel hotkey back out to the worker card; otherwise an opener
  // descends into its submenu. The opener is read from the active resolved face, so a held modifier
  // (a control group on the same key) never triggers navigation. SC2 only.
  const drillNav = useCallback(
    (code: string) => {
      if (!sc2) return;
      const parent = parentContext(roster, context);
      if (parent && (code === "Escape" || cancelCodes.includes(code))) {
        setContextSel(parent);
        return;
      }
      const opens = openerFor(activeModifierLayer[code]?.binding?.cmd);
      if (opens) setContextSel(opens);
    },
    [sc2, roster, context, cancelCodes, activeModifierLayer, setContextSel],
  );

  // the physical-keyboard path into the same navigation, gated like the readout listener
  useEffect(() => {
    if (!sc2 || modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target) || e.repeat) return;
      drillNav(e.code);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sc2, modalOpen, drillNav]);

  return drillNav;
}
